/**
 * TourContext (session 126q) — app-level orchestrator for the
 * InteractiveTour.
 *
 * The tour navigates the user across multiple top-level routes
 * (/, /search, /groups, /places, /add-contacts). If the tour
 * component is mounted inside a page-scoped component, the first
 * route transition unmounts it (and the spotlight) the moment the
 * user clicks "Next". By exposing tour control through a context
 * that's provided at the App level, the tour itself can live in
 * the layout (which survives all route changes) while ANY page
 * component can ask to start it.
 *
 * Usage:
 *   const { openTour, closeTour } = useTour();
 *   <Button onClick={openTour}>Take a tour</Button>
 */
import { createContext, useContext, useState, useCallback } from 'react';

const TourContext = createContext({
  tourOpen: false,
  openTour: () => {},
  closeTour: () => {},
});

export function TourProvider({ children }) {
  const [tourOpen, setTourOpen] = useState(false);
  const openTour = useCallback(() => setTourOpen(true), []);
  const closeTour = useCallback(() => setTourOpen(false), []);
  return (
    <TourContext.Provider value={{ tourOpen, openTour, closeTour }}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  return useContext(TourContext);
}
